import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { StyleClassModule } from 'primeng/styleclass';
import { Sidebar } from 'primeng/sidebar';
import { Router, RouterLink } from '@angular/router';
import { sideBar } from '../../core/constants/sidebar.const';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModuleConst } from '../../core/constants/keys.constant';
import { SidebarService } from '../../services/sidebar.service';
import { AuthService } from '../../services/auth.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LoginResponse } from '../../models/auth.model';
import { CommonModule } from '@angular/common';
import { SideBar } from '../../models/sideBar.model';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DashboardService } from '../../services/dashboard.service';
import { StorageService } from '../../services/StorageService.service';
import { EncryptionService } from '../../services/encryption.service';
import { SidebarHandlingService } from '../../services/sidebarHandling.service';
@Component({
  selector: 'logibids-sidebar',
  standalone: true,
  imports: [
    SidebarModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    StyleClassModule,
    RouterLink,
    TranslateModule,
    CommonModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  isActive = false;
  @ViewChild('sidebarRef') sidebarRef!: Sidebar;
  expandedSubMenus: Set<string> = new Set<string>();

  moduleConst = ModuleConst;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected sideBar = sideBar;
  sidebarVisible: boolean = true;
  private subscriptionSubject$: Subscription;
  profile!: LoginResponse;
  constructor(
    private sidebarService: SidebarService,
    private _authService: AuthService,
    private confirmationService: ConfirmDialogService,
    private translate: TranslateService,
    private dialogRef: DynamicDialogRef,
    private router: Router,
    private _dashboard: DashboardService,
    private storageService: StorageService,
    private _SidebarHandlingService: SidebarHandlingService,
    private _encyrption: EncryptionService
  ) {
    this.sidebarService.sidebarOpen$.subscribe(isOpen => {
      this.sidebarVisible = isOpen;
    });
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profile = res;
        } else {
          this.getProfile();
        }
      });
    this.subscriptionSubject$ = this._dashboard.sidebarChange
      .pipe(takeUntil(this.destroy))
      .subscribe((res: boolean | null) => {
        if (res !== null) {
          this.restoreActiveState();
        }
      });
    this.restoreActiveState();
  }

  ngOnInit(): void {
    const profile = this._encyrption.decryptLocalStorage('profile') || {};
    if (profile.type === 'SUB_ADMIN' && profile.access_role.permissions) {
      this.sideBar = this._SidebarHandlingService.filterSidebarData(
        this.sideBar,
        profile.access_role.permissions
      );
    }
  }

  getProfile(): void {
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res) {
          this.profile = res;
          this._authService.profileDetails.next(res);
          this._encyrption.encryptLocalStorage(res, 'profile');
          if (this.profile.type === 'SUB_ADMIN') {
            this.updateSidebar(res.access_role?.permissions || []);
          }
        }
      });
  }

  updateSidebar(permissions: SideBar[]) {
    this.sideBar = this._SidebarHandlingService.filterSidebarData(
      this.sideBar,
      permissions
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deSelectItems(item: any) {
    const moduleKey = item.sidebar_module;
    if (this.expandedSubMenus.has(moduleKey)) {
      this.expandedSubMenus.delete(moduleKey);
    } else {
      this.expandedSubMenus.add(moduleKey);
    }
  }

  toggleActive(
    item: SideBar,
    subItems: SideBar | null,
    subSubItem: SideBar | null = null
  ): void {
    this.sideBar.forEach(val => {
      val.active =
        val.sidebar_module === item.sidebar_module && !item.isSubMenu;

      val.subMenuItems?.forEach(list => {
        list.active =
          item.isSubMenu && subItems?.sidebar_module === list.sidebar_module;

        list.subMenuItems?.forEach(res => {
          res.active =
            item.isSubMenu && subSubItem?.sidebar_module === res.sidebar_module;
        });

        list.subModules?.forEach(res => {
          res.active =
            item.isSubMenu && subSubItem?.sidebar_module === res.sidebar_module;
        });
      });
    });
    const activeState = {
      parentId: item.sidebar_module,
      subItemId: subItems?.sidebar_module || null,
      subSubItemId: subSubItem?.sidebar_module || null,
      isSubMenu: item.isSubMenu || false,
    };

    if (item.isSubMenu) {
      this.expandedSubMenus.add(item.sidebar_module);
      if (subItems) {
        this.expandedSubMenus.add(subItems.sidebar_module);
      }
    }
    this.storageService.updateStorage(JSON.stringify(activeState));
  }

  restoreActiveState(): void {
    const savedState = localStorage.getItem('activeState');
    if (!savedState) return;

    const { parentId, subItemId, subSubItemId, isSubMenu } =
      JSON.parse(savedState);
    let navigated = false;

    for (const val of this.sideBar) {
      const isParentMatch = val.sidebar_module === parentId;
      val.active = isParentMatch && !isSubMenu;

      if (isParentMatch && isSubMenu) {
        this.expandedSubMenus.add(val.sidebar_module);
      }

      if (val.subMenuItems && val.subMenuItems.length) {
        for (const list of val.subMenuItems) {
          const isSubMatch = list.sidebar_module === subItemId;
          list.active = isSubMatch;

          if (isSubMatch && isSubMenu) {
            this.expandedSubMenus.add(list.sidebar_module);
          }

          if (list.subMenuItems && list.subMenuItems.length) {
            for (const res of list.subMenuItems) {
              const isSubSubMatch = res.sidebar_module === subSubItemId;
              res.active = isSubSubMatch;

              if (isSubSubMatch && isSubMenu) {
                this.expandedSubMenus.add(list.sidebar_module);

                // Navigate to sub-sub menu route
                this.router.navigate([res.path]);
                navigated = true;
                break;
              }
            }

            if (navigated) break;
          }

          if (list.subModules && list.subModules.length) {
            for (const res of list.subModules) {
              const isSubSubMatch = res.sidebar_module === subSubItemId;
              res.active = isSubSubMatch;

              if (isSubSubMatch && isSubMenu) {
                this.expandedSubMenus.add(list.sidebar_module);

                // Navigate to sub-sub menu route
                this.router.navigate([res.path]);
                navigated = true;
                break;
              }
            }

            if (navigated) break;
          }

          if (isSubMatch && list.path) {
            this.router.navigate([list.path]);
            navigated = true;
            break;
          }
        }

        if (navigated) break;
      }

      if (isParentMatch && val.path && !isSubMenu) {
        this.router.navigate([val.path]);
        break;
      }
    }
  }

  onLogoClick(): void {
    const dashboardItem = this.sideBar.find(
      item => item.sidebar_module === this.moduleConst.Dashboard
    );
    if (dashboardItem) {
      this.toggleActive(dashboardItem, null);
    }
  }

  btn_logOut(): void {
    this.confirmationService
      .showConfirmationDialog(' ', this.translate.instant('SureLogout'))
      .then(result => {
        if (result) {
          this.router.navigate(['/auth/login']);
          this.dialogRef.close();
          localStorage.clear();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
